// Shared handling of the "this document is no longer in storage" case.
//
// The frontend used to never see this error at all: /document/download/{id}
// answers 200 with an absolute S3 url, and the missing object is only hit inside
// the tab opened by window.open, where the user is left with Amazon's raw
// NoSuchKey XML. The backend now checks the object exists and answers 404, so
// every view/download call finally has something real to catch, and this module
// is what it catches it with.
//
// It is written so that a backend that does not validate yet keeps behaving
// exactly as before: nothing here runs on the happy path.

// A file that is gone comes back as 404 (410 if it was deliberately purged).
const MISSING_STATUS = [404, 410];

// Already handled by the axios interceptor, which replaces the page with the
// login screen. Showing a dialog on top of that redirect only gets in the way.
const SESSION_STATUS = [401, 419];

function statusOf (error) {
  return error && error.response ? error.response.status : null;
}

// The error body arrives in two shapes: plain JSON, and — when the request used
// responseType 'blob' — that same JSON wrapped in a Blob, which has to be read
// asynchronously before it can be inspected. Anything unparseable resolves to
// null on purpose: this runs inside a .catch and must not throw a second error
// on top of the first one.
export async function readErrorBody (error) {
  const data = error && error.response ? error.response.data : null;

  if (!data) return null;

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch (e) {
      return null;
    }
  }

  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  }

  return data;
}

export function isFileMissing (error, body) {
  if (MISSING_STATUS.indexOf(statusOf(error)) !== -1) return true;

  return !!(body && body.code === "FILE_NOT_FOUND");
}

export function isSessionError (error) {
  return SESSION_STATUS.indexOf(statusOf(error)) !== -1;
}

// The header is only sent when the backend whitelists it in
// Access-Control-Expose-Headers. It used to be read blind, so a missing header
// threw inside the .then() and skipped the isLoadingOff below it, leaving the
// overlay on with no way back.
export function downloadFileName (response, fallback) {
  const disposition = response.headers["content-disposition"];

  if (!disposition || disposition.indexOf("filename=") === -1) {
    return fallback;
  }

  return disposition.split("filename=")[1].replace(/"/g, "").trim();
}

// Mixin for any view that opens or downloads documents. "warning" and not
// "danger" because it reports a real consequence (the file is not available any
// more), not a destructive action the user is about to take.
export const fileErrorHandling = {
  methods: {
    // The title is optional: vuejs-dialog only renders the h6 when one is
    // given, and the stylesheet already places the icon next to the message
    // when there is no title. A generic failure reads better without one than
    // under a heading that contradicts the warning glyph.
    _fileDialog (body, titleKey) {
      const message = { body: body };

      if (titleKey) message.title = this.$t(titleKey);

      this.$dialog.alert(message, { customClass: "cd-dialog cd-dialog--warning" });
    },
    // names: the documents known to be gone. Empty when the call that failed
    // did not carry a name worth showing.
    showFileMissing (names) {
      const list = (names || []).filter(Boolean);

      if (list.length > 1) {
        this._fileDialog(this.$t("file_not_found_multiple") + " " + list.join(", "), "file_not_found_title");
        return;
      }

      this._fileDialog(
        list.length ? list[0] + ": " + this.$t("file_not_found") : this.$t("file_not_found"),
        "file_not_found_title"
      );
    },
    // "session" means the axios interceptor is already replacing the page with
    // the login screen, and anything drawn on top of that only gets in the way.
    // Callers that batch several files use this directly and report at the end;
    // the rest go through onFileError.
    async classifyFileError (error) {
      if (isSessionError(error)) return "session";

      return isFileMissing(error, await readErrorBody(error)) ? "missing" : "failed";
    },
    // Single entry point for the .catch of every view/download call. Awaiting it
    // is optional: callers that only need the dialog can fire and forget.
    async onFileError (error, name) {
      const kind = await this.classifyFileError(error);

      if (kind === "missing") this.showFileMissing([name]);
      else if (kind === "failed") this._fileDialog(this.$t("file_download_error"));

      return kind;
    }
  }
};
